import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearYourTownloopSearchValue } from '@/store/slices/searchBarSlice';
import { eventBus } from '@/utils/eventBus';
import { useNavigate } from 'react-router-dom';
import styles from './Cart.module.scss';
import { Checkbox,App,Modal } from 'antd';
import CartListGroup, { CartShop } from '../../components/CartListGroup/CartListGroup';
import PaymentPanelGroup from '../../components/PaymentPanelGroup/PaymentPanelGroup';
import { getMyCartList, deleteCartProduct } from '@/api/cart';
import { deliveryMethodOptionsMapper } from '@/utils/constants';
import { useTranslation } from "react-i18next";

const Cart: React.FC = () => {

  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const yourTownloopSearchValue = useAppSelector((state) => state.searchBar.yourTownloopSearchValue);

  // 购物车列表
  const [shops, setShops] = useState<CartShop[]>([]);

  //是否展示Payment Breakdown
  const [showPanel, setShowPanel] = useState(true);
  
  //是否是手机端
  const isMobile = useAppSelector((state) => state.searchBar.isMobile);

  // 获取购物车数据
  const fetchCartData = useCallback(async () => {
    try {
      const res = await getMyCartList({});
      if (res.code === 200) {
        // 将接口数据转换为组件需要的格式
        const cartData = res.data || [];
        //console.log("cart Data  === ", cartData)
        // 按店铺分组购物车数据
        const shopMap = new Map();
        
        cartData.forEach((cartItem: any) => {
          const product = cartItem.product;
          const productSku = cartItem.product_sku;
          const cartId = cartItem.cart_id; // 获取购物车ID
          const merchant = cartItem.merchant;
          
          console.log('Merchant info:', merchant);
          // 店铺信息进行分组
          const shopId = merchant?.merchant_id || product.product_id;
          const shopName = merchant?.merchant_name || product.product_category?.product_category_name || 'Unknown Store';
          
          if (!shopMap.has(shopId)) {
            shopMap.set(shopId, {
              shopId: shopId,
              shopName: shopName,
              checked: true, // 默认选中
              products: []
            });
          }
          const shop = shopMap.get(shopId);
          const productImage = product.product_media_list?.[0]?.product_media_file_url || '';
          
          // 由于 product_sku 可能为 null，我们需要处理这种情况
          let matchedSku = null;
          if (productSku && productSku.product_sku_id) {
            matchedSku = product.product_sku_values?.find((sku: any) => sku.product_sku_id === productSku.product_sku_id);
          }
          
          if (!matchedSku && product.product_sku_values && product.product_sku_values.length > 0) {
            matchedSku = product.product_sku_values[0];
          }
          
          if (matchedSku) {
            const price = matchedSku.product_price || '0.00';
            const skuId = matchedSku.product_sku_id || '';
            const specs = matchedSku.specs || [];
            const stock = matchedSku.product_stock || 0;
            
            // 构建自定义属性
            const customAttributes = product.product_custom_attributes || {};
            const customAttrText = Object.keys(customAttributes).length > 0 
              ? Object.entries(customAttributes).map(([key, value]) => `${key}: ${value}`).join(', ')
              : '';
            
            // 处理交货方式
            const deliveryMethod = product.delivery_method; 
            const deliveryMethodOptions = deliveryMethodOptionsMapper(t);
            const deliveryMethodText = deliveryMethodOptions.find(option => option.value === deliveryMethod)?.label;

            const productData = {
              id: `${product.product_id}_${skuId}`, // 使用商品ID和SKU ID组合作为唯一标识
              cart_id: cartId, // 添加购物车ID
              img: productImage,
              title: product.product_title,
              price: price,
              color: specs, // 将规格信息作为数组传递
              quantity: cartItem.quantity || 1,
              checked: true,
              payType: t('Cart.PayByCreditCard'),
              services: [ t('Cart.FastRefunds')],
              purchaseLimit: `${t('Cart.PurchaseLimit')}: ${stock} ${t('Cart.Pieces')}`,
              promotion: '',
              maxQuantity: stock,
              skuId: skuId,
              customAttributes: customAttrText,
              deliveryMethod: deliveryMethod,
              deliveryMethodText: deliveryMethodText
            };
            
            console.log('Product data to add:', productData); // 调试：查看要添加的商品数据
            shop.products.push(productData);
          } else {
            console.warn('No matched SKU found for product:', product.product_id); // 调试：警告没有匹配的SKU
          }
        });
        
        const formattedShops: CartShop[] = Array.from(shopMap.values());
        setShops(formattedShops);
      } else {
      }
    } catch (error) {
    }
  }, [message]);

  // 初始化时获取数据
  useEffect(() => {
    fetchCartData();
  }, [fetchCartData]);

  // 检查是否有数据
  const hasData = shops.length > 0;

  // 全选
  const allChecked = hasData && shops.every(shop => shop.checked);

  // 全选事件
  const handleAllCheck = (checked: boolean) => {
    if (!hasData) return;
    
    setShops(shops => shops.map(shop => ({
      ...shop,
      checked,
      products: shop.products.map(p => ({ ...p, checked })),
    })));
  };

  // 店铺选
  const handleShopCheck = useCallback((shopId: string, checked: boolean) => {
    if (!hasData) return;
    
    setShops(shops => shops.map(shop => shop.shopId === shopId ? {
      ...shop,
      checked,
      products: shop.products.map(p => ({ ...p, checked })),
    } : shop));
  }, [hasData]);

  // 商品选
  const handleProductCheck = useCallback((shopId: string, productId: string, checked: boolean) => {
    if (!hasData) return;
    
    setShops(shops => shops.map(shop => {
      if (shop.shopId !== shopId) return shop;
      const newProducts = shop.products.map(p => p.id === productId ? { ...p, checked } : p);
      const shopChecked = newProducts.every(p => p.checked);
      return { ...shop, products: newProducts, checked: shopChecked };
    }));
  }, [hasData]);

  // 数量加减
  const handleQuantityChange = useCallback((shopId: string, productId: string, quantity: number) => {
    if (!hasData) return;

    setShops(shops => shops.map(shop => {
      if (shop.shopId !== shopId) return shop;
      return {
        ...shop,
        products: shop.products.map(p => p.id === productId ? { ...p, quantity: Math.max(1, quantity) } : p),
      };
    }));
  }, [hasData]);

  // 删除
  const handleDelete = useCallback(async (shopId: string, productId: string) => {
    if (!hasData) return;
    
    // 找到要删除的商品信息
    const shop = shops.find(s => s.shopId === shopId);
    const product = shop?.products.find(p => p.id === productId);
    
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete "${product?.title || 'this item'}" from your cart?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // 从组合ID中提取原始商品ID
          const originalProductId = productId.split('_')[0];
          
          // 调用删除接口
          const params = {
            product_list: [{
              product_id: originalProductId,
              product_sku_id: product?.skuId || '',
              quantity: product?.quantity || 1,
            }]
          };
          const res = await deleteCartProduct(params);
          if (res.code === 200) {
            // 删除成功后重新获取购物车数据
            await fetchCartData();
            
            message.success('Item has been removed from your cart');
          } else {
            message.error(res.message || 'Delete failed');
          }
        } catch (error) {
          console.error('Delete cart item error:', error);
          message.error('Delete failed');
        }
      },
    });
  }, [hasData, shops]);

  // 批量删除选中的商品
  const handleBatchDelete = () => {

    // 检查是否有选中的商品
    let isChecked = false;

    for (const shop of shops) {
      if (shop.checked) {
        isChecked = true;
        break;
      }
      for (const product of shop.products) {
        if (product.checked) {
          isChecked = true;
          break;
        }
      }
      if (isChecked) break; // 额外保险
    }

    if (!isChecked) {
      message.error('Please select items to delete first');
      return;
    }

    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete the selected items? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // 收集所有选中的商品
          const selectedProducts: any[] = [];
          
          shops.forEach(shop => {
            shop.products.forEach(product => {
              if (product.checked) {
                // 从组合ID中提取原始商品ID
                const originalProductId = product.id.split('_')[0];
                selectedProducts.push({
                  product_id: originalProductId,
                  product_sku_id: product.skuId || '',
                  quantity: product.quantity
                });
              }
            });
          });

          // 调用删除接口
          const params = {
            product_list: selectedProducts
          };
          
          const res = await deleteCartProduct(params);
          if (res.code === 200) {
            // 批量删除成功后重新获取购物车数据
            await fetchCartData();
          
            message.success('Selected items have been deleted successfully');
          } else {
            message.error(res.message || 'Delete failed');
          }
        } catch (error) {
          console.error('Batch delete cart items error:', error);
          message.error('Delete failed');
        }
      },
    });
    
  }

  // 收藏
  const handleCollect = useCallback((shopId: string, productId: string) => {
    if (!hasData) return;
    
    alert('Added to favorites successfully!');
  }, [hasData]);

  // 结算按钮
  const handleCheckout = () => {
    // 所有选中的商品
    const selectedProducts: any[] = [];
    const selectedDeliveryMethods: number[] = [];
    
    shops.forEach(shop => {
      shop.products.forEach(product => {
        if (product.checked) {
          const originalProductId = product.id.split('_')[0];
          selectedProducts.push({
            cart_id: product.cart_id, // 添加购物车ID
            product_id: originalProductId,
            quantity: product.quantity,
            product_sku_id: product.skuId || '',
          });
          // 收集交货方式
          if (product.deliveryMethod) {
            selectedDeliveryMethods.push(product.deliveryMethod);
          }
        }
      });
    });

    if (selectedProducts.length === 0) {
      message.error('Please select items to checkout');
      return;
    }

    // 检查交货方式是否一致
    if (selectedDeliveryMethods.length > 0) {
      const uniqueDeliveryMethods = Array.from(new Set(selectedDeliveryMethods));
      if (uniqueDeliveryMethods.length > 1) {
        const deliveryMethodOptions = deliveryMethodOptionsMapper(t);
        const methodTexts = uniqueDeliveryMethods.map(method => 
          deliveryMethodOptions.find(option => option.value === method)?.label || 'Unknown'
        );
        message.error(`Cannot checkout items with different delivery methods: ${methodTexts.join(', ')}. Please select items with the same delivery method.`);
        return;
      }
    }

    console.log('selectedProducts:', selectedProducts);
    const encodedData = btoa(JSON.stringify(selectedProducts));
    navigate(`/PlaceOrder?productsToOrder=${encodedData}`);
  };

  // 计算总价
  const total = shops
    .flatMap(shop => shop.products)
    .filter(p => p.checked)
    .reduce((sum, p) => sum + (Number(p.price.replace('$', '')) * p.quantity), 0)
    .toFixed(2);

  const selectedImages = shops
    .flatMap(shop => shop.products)
    .filter(p => p.checked)
    .map(p => p.img);

  // 计算选中商品数量
  const selectedCount = shops
    .flatMap(shop => shop.products)
    .filter(p => p.checked)
    .length;

  useEffect(() => {
    return () => {
      eventBus.emit('deleteTownloopSearchValue');
      dispatch(clearYourTownloopSearchValue());
    };
  }, []);

  useEffect(() => {
    // 监听搜索关键字
  }, [yourTownloopSearchValue]);

  // 如果购物车为空，显示空状态
  if (shops.length === 0) {
    return ( 
      <div className={styles['cart-root']}>
        <div className={styles['cart-toolbar']}>
          <Checkbox checked={false} disabled>{t('Cart.CheckAll')}</Checkbox>
          {/* <button className={`ant-btn ${styles.actionButton}`} disabled>Bookmark </button> */}
          <button className={`ant-btn ${styles.actionButton}`} disabled>{t('Cart.Delete')}</button>
          <div className={styles.flexSpacer} />
          {!isMobile && (
            <div className={styles.paymentButtonContainer}>
              <button
                className={`ant-btn ${styles.PaymentBtn}`}
                disabled
              >
                {t('Cart.PaymentBreakdown')}
                <span className={styles.paymentButtonArrow}>▼</span>
              </button>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div style={{ fontSize: '18px', marginBottom: '10px' }}>{t('Cart.cartempty')}</div>
          <div style={{ color: '#999' }}>{t('Cart.Additems')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['cart-root']}>
      <div className={styles['cart-toolbar']}>
        <Checkbox checked={allChecked} onChange={e => handleAllCheck(e.target.checked)}>{t('Cart.CheckAll')}</Checkbox>
        {/* <button className={`ant-btn ${styles.actionButton}`}>Bookmark </button> */}
        <button className={`ant-btn ${styles.actionButton}`} onClick={handleBatchDelete}>{t('Cart.BulkDelete')}</button> 
        <div className={styles.flexSpacer} />
        {!isMobile && (
          <div className={styles.paymentButtonContainer}>
            <button
              className={`ant-btn ${styles.PaymentBtn}`}
              onClick={() => setShowPanel(true)}
            >
              {t('Cart.PaymentBreakdown')}
              <span className={styles.paymentButtonArrow}>▼</span>
            </button>
            <PaymentPanelGroup
              visible={showPanel}
              onClose={() => setShowPanel(false)}
              total={total}
              selectedImages={selectedImages}
              onCheckout={handleCheckout}
            />
          </div>
          
        )}
      </div>
      <CartListGroup
        shops={shops}
        onShopCheck={handleShopCheck}
        onProductCheck={handleProductCheck}
        onQuantityChange={handleQuantityChange}
        onDelete={handleDelete}
        onCollect={handleCollect}
      />
      {isMobile && (
        <div className={styles['cart-settle-bar']}>
          <div className={styles['cart-settle-left']}>
            <Checkbox checked={allChecked} onChange={e => handleAllCheck(e.target.checked)}>
              {t('Cart.All')}
            </Checkbox>
          </div>
          <div className={styles['cart-settle-right']}>
            <div className={styles['cart-settle-total']}>
              {t('Cart.Total')}: ${total}
            </div>
            <button 
              className={styles['cart-settle-btn']}
              disabled={selectedCount === 0}
              onClick={handleCheckout}
            >
              {t('Cart.Checkout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart; 